import React, { FC, ReactNode } from "react";
import { useQueryHandler } from "@sinch/hooks/useQueryHandler";
import { useHoverCard } from "@sinch/components/application/HoverCard/useHoverCard";
import {
  ARROW_CLASS,
  TooltipArrow,
  TooltipPaper,
  TooltipPopper,
} from "@sinch/components/application/HoverCard/styledComponents";
import { CompanyTooltip as CompanyTooltipQueryDoc } from "./queries.graphql";
import { CompanyTooltipQuery, CompanyTooltipQueryVariables } from "./queries.types";
import { CompanyTooltipContent } from "./CompanyTooltipContent";

export interface CompanyProfileTooltipProps {
  /**
   * Id of the company whose profile card is shown.
   */
  companyId: string;
  /**
   * Existing DOM element to anchor the card to (e.g. the web-component host that captured
   * the trigger markup). Takes precedence over `children`; when omitted, the first child
   * element is used as the anchor instead.
   */
  anchorElement?: HTMLElement;
  /**
   * Trigger rendered inline; the card anchors to the first child element. That child must
   * forward its `ref` to a DOM node. Ignored when `anchorElement` is provided.
   */
  children?: ReactNode;
}

/**
 * Renders the company profile card (`CompanyHeader`) inside an MUI Popper on hover. The card
 * anchors either to an explicit `anchorElement` (e.g. a web-component host) or, when that is
 * omitted, to the first child element passed in - so the same component works both as a
 * web-component view and as an inline wrapper around a trigger. Company data is fetched lazily
 * the first time the tooltip opens and is then served from the Apollo cache on subsequent opens.
 */
export const CompanyProfileTooltip: FC<CompanyProfileTooltipProps> = ({ companyId, anchorElement, children }) => {
  const { trigger, hasOpened, popperProps, paperProps, arrowRef } = useHoverCard({ anchorElement, children });

  const { data } = useQueryHandler<CompanyTooltipQuery, CompanyTooltipQueryVariables>(
    CompanyTooltipQueryDoc,
    { id: companyId },
    { skip: !hasOpened }
  );

  return (
    <>
      {trigger}
      <TooltipPopper {...popperProps}>
        <TooltipPaper {...paperProps}>
          <TooltipArrow ref={arrowRef} className={ARROW_CLASS} />
          <CompanyTooltipContent company={data?.company} />
        </TooltipPaper>
      </TooltipPopper>
    </>
  );
};
